import React, { useEffect, useRef, useState } from "react";
import { Button, Dialog, Text, Tile } from "@wakelet/ui-kit";
import { translateKey } from "../language/translate";
import { CentralContent } from "../central-content/CentralContent";
import { getAccountApiUrl } from "../../env/env";
import { useAuth } from "../auth/AuthContext";
import { Spinner } from "../loading/Spinner";

const exportURL = getAccountApiUrl("/exports");
const INITIAL_CHECK_TIMEOUT_MS = 5000;
const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 12;

type ExportEntry = {
  id: string;
  href: string;
  created: string;
  size?: number;
  type?: string;
};

type ExportRequest = {
  artifactId: string;
};

const download = (url: string) => {
  window.location.href = url;
};

const responseError = (response: Response, action: string) => {
  if (response.status === 429) {
    return new Error("Too many export requests. Please try again shortly.");
  }
  return new Error(`Unable to ${action}.`);
};

const retryAfterError = (retryAfter: string | null): Error | null => {
  if (retryAfter === null || retryAfter.trim() === "") {
    return null;
  }

  const seconds = Number(retryAfter);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }

  const totalSeconds = Math.ceil(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  const minuteLabel = minutes === 1 ? "minute" : "minutes";
  const secondLabel = remainingSeconds === 1 ? "second" : "seconds";
  return new Error(
    `Please wait ${minutes} ${minuteLabel} and ${remainingSeconds} ${secondLabel} before creating another export.`,
  );
};

const exportDescription = () =>
  translateKey(
    "settings:manageAccount.downloadYourInformation.inJsonAtAnyTime",
  ).replace(/\.$/, "");

export const ExportContent: React.FC = () => {
  const { user } = useAuth();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportUrl, setExportUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [active, setActive] = useState(false);
  const [checkingExistingExports, setCheckingExistingExports] = useState(false);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);
  const [creatingExport, setCreatingExport] = useState(false);
  const [polling, setPolling] = useState(false);
  const [refreshingExport, setRefreshingExport] = useState(false);
  const [availableExports, setAvailableExports] = useState<ExportEntry[]>([]);
  const [pendingArtifactId, setPendingArtifactId] = useState<string | null>(
    null,
  );
  const activeRef = useRef(false);
  const mountedRef = useRef(true);
  const operationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const initialCheckCompleteRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearInitialCheckTimer = () => {
    if (initialCheckTimerRef.current !== null) {
      clearTimeout(initialCheckTimerRef.current);
      initialCheckTimerRef.current = null;
    }
  };

  const stopPolling = () => {
    clearTimer();
    if (mountedRef.current) {
      setPolling(false);
    }
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
      activeRef.current = false;
      clearTimer();
      clearInitialCheckTimer();
    };
  }, []);

  const isCurrentOperation = (operationId: number) =>
    mountedRef.current &&
    activeRef.current &&
    operationRef.current === operationId;

  const getHeaders = async () => ({
    Authorization: `Bearer ${await user?.getIdToken()}`,
  });

  const listExports = async (): Promise<ExportEntry[]> => {
    let response: Response;
    try {
      response = await fetch(exportURL, { headers: await getHeaders() });
    } catch {
      throw new Error("Unable to check your exports.");
    }
    if (!response.ok) {
      throw responseError(response, "check your exports");
    }
    const body = await response.json();
    return Array.isArray(body?.exports) ? body.exports : [];
  };

  const poll = async (
    artifactId: string,
    operationId: number,
    pollNumber: number,
  ) => {
    if (!isCurrentOperation(operationId)) {
      return;
    }

    try {
      const exports = await listExports();
      if (!isCurrentOperation(operationId)) {
        return;
      }

      const completed = exports.find(
        (entry) => entry.id === artifactId && !!entry.href,
      );
      if (completed) {
        stopPolling();
        setPendingArtifactId(null);
        setExportUrl(completed.href);
        setErrorMessage("");
        return;
      }
    } catch {
      if (!isCurrentOperation(operationId)) {
        return;
      }
    }

    if (pollNumber >= MAX_POLLS) {
      stopPolling();
      setErrorMessage(
        "Your export is taking longer than expected. Refresh to check again.",
      );
      return;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void poll(artifactId, operationId, pollNumber + 1);
    }, POLL_INTERVAL_MS);
  };

  const startPolling = (artifactId: string, operationId: number) => {
    clearTimer();
    setPendingArtifactId(artifactId);
    setPolling(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void poll(artifactId, operationId, 1);
    }, POLL_INTERVAL_MS);
  };

  const completeInitialCheck = (operationId: number) => {
    if (!isCurrentOperation(operationId) || initialCheckCompleteRef.current) {
      return;
    }
    initialCheckCompleteRef.current = true;
    clearInitialCheckTimer();
    setCheckingExistingExports(false);
    setInitialCheckComplete(true);
  };

  const checkExistingExports = (operationId: number) => {
    initialCheckTimerRef.current = setTimeout(() => {
      initialCheckTimerRef.current = null;
      completeInitialCheck(operationId);
    }, INITIAL_CHECK_TIMEOUT_MS);

    void listExports()
      .then((exports) => {
        if (
          isCurrentOperation(operationId) &&
          !initialCheckCompleteRef.current
        ) {
          setAvailableExports(exports.filter((entry) => !!entry.href));
        }
      })
      .catch((error) => {
        if (
          isCurrentOperation(operationId) &&
          !initialCheckCompleteRef.current
        ) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Unable to check your exports.",
          );
        }
      })
      .finally(() => completeInitialCheck(operationId));
  };

  const openExportDialog = () => {
    if (activeRef.current) {
      return;
    }

    const operationId = operationRef.current + 1;
    operationRef.current = operationId;
    activeRef.current = true;
    setActive(true);
    setCheckingExistingExports(true);
    setInitialCheckComplete(false);
    initialCheckCompleteRef.current = false;
    setCreatingExport(false);
    setPolling(false);
    setRefreshingExport(false);
    setAvailableExports([]);
    setErrorMessage("");
    setExportUrl("");
    setPendingArtifactId(null);
    setExportDialogOpen(true);
    clearTimer();
    clearInitialCheckTimer();
    checkExistingExports(operationId);
  };

  const createExport = async () => {
    if (!activeRef.current || !initialCheckCompleteRef.current) {
      return;
    }

    const operationId = operationRef.current;
    setCreatingExport(true);
    setErrorMessage("");

    try {
      const response = await fetch(exportURL, {
        headers: await getHeaders(),
        method: "POST",
      });
      if (!response.ok) {
        if (response.status === 429) {
          throw (
            retryAfterError(response.headers.get("Retry-After")) ??
            responseError(response, "start your export")
          );
        }
        throw responseError(response, "start your export");
      }

      const request = (await response.json()) as ExportRequest;
      if (!request?.artifactId) {
        throw new Error("Unable to start your export.");
      }
      if (!isCurrentOperation(operationId)) {
        return;
      }

      setCreatingExport(false);
      startPolling(request.artifactId, operationId);
    } catch (error) {
      if (!isCurrentOperation(operationId)) {
        return;
      }
      stopPolling();
      setCreatingExport(false);
      setPendingArtifactId(null);
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to start your export.",
      );
    }
  };

  const refreshPendingExport = async () => {
    if (polling || !pendingArtifactId || !activeRef.current) {
      return;
    }

    const operationId = operationRef.current;
    const artifactId = pendingArtifactId;
    setRefreshingExport(true);
    setErrorMessage("");

    try {
      const exports = await listExports();
      if (!isCurrentOperation(operationId)) {
        return;
      }

      setAvailableExports(exports.filter((entry) => !!entry.href));
      const completed = exports.find(
        (entry) => entry.id === artifactId && !!entry.href,
      );
      if (completed) {
        setPendingArtifactId(null);
        setExportUrl(completed.href);
      } else {
        setErrorMessage(
          "Your export is still being generated. Refresh to check again.",
        );
      }
    } catch (error) {
      if (isCurrentOperation(operationId)) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Unable to check your exports.",
        );
      }
    } finally {
      if (isCurrentOperation(operationId)) {
        setRefreshingExport(false);
      }
    }
  };

  const closeDialog = () => {
    operationRef.current += 1;
    activeRef.current = false;
    clearTimer();
    clearInitialCheckTimer();
    setActive(false);
    setCheckingExistingExports(false);
    setInitialCheckComplete(false);
    initialCheckCompleteRef.current = false;
    setCreatingExport(false);
    setPolling(false);
    setRefreshingExport(false);
    setAvailableExports([]);
    setPendingArtifactId(null);
    setErrorMessage("");
    setExportUrl("");
    setExportDialogOpen(false);
  };

  return (
    <Tile>
      <Dialog
        title={exportDescription()}
        open={exportDialogOpen}
        onClose={closeDialog}
        data-testid="export-dialog"
      >
        <CentralContent fullWidth className="min-h-0 flex-1 overflow-y-auto">
          <div className="w-full flex flex-col">
            {(checkingExistingExports ||
              creatingExport ||
              polling ||
              refreshingExport) &&
              !exportUrl && <Spinner />}
            {errorMessage && (
              <Text.Paragraph data-testid="export-error">
                {errorMessage}
              </Text.Paragraph>
            )}
            {exportUrl && (
              <Button
                onClick={() => download(exportUrl)}
                className="max-w-full"
                data-testid="download-btn-in-dialog"
              >
                <Button.Label
                  text={translateKey(
                    "settings:manageAccount.downloadYourInformation.downloadJsonFile",
                  )}
                />
              </Button>
            )}
            {pendingArtifactId && !exportUrl && (
              <div className="flex justify-end mt-3">
                <Button
                  onClick={() => void refreshPendingExport()}
                  disabled={polling || refreshingExport}
                  data-testid="refresh-export-btn-in-dialog"
                >
                  <Button.Label text="Refresh" />
                </Button>
              </div>
            )}
            {initialCheckComplete &&
              !exportUrl &&
              !pendingArtifactId &&
              availableExports.length > 0 && (
                <div
                  className="w-full bg-white rounded p-4"
                  data-testid="available-exports"
                >
                  <Text.Label>Available files</Text.Label>
                  <div className="flex flex-wrap items-start gap-2 mt-2 max-h-64 overflow-y-auto">
                    {availableExports.map((entry) => (
                      <Button
                        key={entry.id}
                        onClick={() => download(entry.href)}
                        className="max-w-full"
                        data-testid={`existing-export-${entry.id}`}
                      >
                        <Button.Label text={entry.created} />
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            {initialCheckComplete && !exportUrl && !pendingArtifactId && (
              <div
                className={`flex justify-end ${availableExports.length > 0 ? "mt-3" : ""}`}
              >
                <Button
                  onClick={() => void createExport()}
                  disabled={!active || creatingExport}
                  data-testid="create-export-btn-in-dialog"
                >
                  <Button.Label
                    text={translateKey(
                      "settings:manageAccount.downloadYourInformation.createJsonFile",
                    )}
                  />
                </Button>
              </div>
            )}
          </div>
        </CentralContent>
      </Dialog>
      <Text.Heading level={3}>{translateKey("account:your.data")}</Text.Heading>
      <Text.Paragraph className="mb-1">{exportDescription()}</Text.Paragraph>
      <div>
        <Button
          onClick={openExportDialog}
          disabled={active || exportDialogOpen || !!pendingArtifactId}
          variant="secondary"
          className="float-right"
          data-testid="trigger-export-btn"
        >
          <Button.Label
            text={translateKey(
              "settings:manageAccount.downloadYourInformation.createJsonFile",
            )}
          />
        </Button>
      </div>
    </Tile>
  );
};
